import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { Parameter } from "../../models/Parameter";
import { AuxiliaryEquipmentService } from "../../services/AuxiliaryEquipmentService";
import { AuxiliaryEquipmentSearchResultDTO } from "../../models/AuxiliaryEquipment/AuxiliaryEquipmentSearchResultDTO";

export default function AuxiliaryEquipmentIndex() {
    const intl = useIntl();
    //Declaração de outros estados e variáveis importantes
    const navigate = useNavigate();
    const auxiliaryEquipmentService = new AuxiliaryEquipmentService();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<AuxiliaryEquipmentSearchResultDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();

    //Vamos tentar obter a page e a pageSize pelos query parameters
    //Usamos useState em vez de simple var, para não estar sempre a obter o valor a cada re-render, desta forma o page e o pageSize são sempre o mesmo
    //porque nunca estamos a fazer setState à var
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(
        Utilities.LoadPageSizeFromURLQuery(pageSizes)
    );

    //Search Parameters
    const [erpId, setErpId] = useState<string>(
        Utilities.LoadParameterFromURLQuery("erpId", "string", "")
    );
    const [description, setDescription] = useState<string>(
        Utilities.LoadParameterFromURLQuery("description", "string", "")
    );
    const [serialNumber, setSerialNumber] = useState<string>(
        Utilities.LoadParameterFromURLQuery("serialNumber", "string", "")
    );
    const [brand, setBrand] = useState<string>(
        Utilities.LoadParameterFromURLQuery("brand", "string", "")
    );
    const [model, setModel] = useState<string>(
        Utilities.LoadParameterFromURLQuery("model", "string", "")
    );

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<
        SortingRule<object>[] | null
    >(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder")
    );
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) =>
                    OperationsCell(props.cell.row.original.erpId, "auxiliaryEquipment"),
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.AuxiliaryEquipments.Table.Column1" />,
                accessor: "erpId",
                width: 25,
            },
            {
                Header: <FormattedMessage id="Screens.AuxiliaryEquipments.Table.Column2" />,
                accessor: "description",
                width: 25,
            },
            {
                Header: <FormattedMessage id="Screens.AuxiliaryEquipments.Table.Column3" />,
                accessor: "serialNumber",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.AuxiliaryEquipments.Table.Column4" />,
                accessor: "brand",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.AuxiliaryEquipments.Table.Column5" />,
                accessor: "model",
                width: 20,
            },
        ],
        []
    );

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl: string = `?page=${page}&pageSize=${pageSize}` +
            `${Utilities.AddParamToQueryIfValue("code", "string", erpId)}` +
            `${Utilities.AddParamToQueryIfValue("description", "string", description)}` +
            `${Utilities.AddParamToQueryIfValue("serialNumber", "string", serialNumber)}` +
            `${Utilities.AddParamToQueryIfValue("brand", "string", brand)}` +
            `${Utilities.AddParamToQueryIfValue("model", "string", model)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/auxiliaryEquipments${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/auxiliaryEquipments${searchUrl}`);
        }

        setLoading(true);

        //Vamos obter os dados
        var response = await auxiliaryEquipmentService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(5);
            setCurrentPage(1);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        //Atribuímos os dados
        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);

        //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("erpId", erpId));
        parameters.push(new Parameter("description", description));
        parameters.push(new Parameter("serialNumber", serialNumber));
        parameters.push(new Parameter("brand", brand));
        parameters.push(new Parameter("model", model));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setErpId("");
        setDescription("");
        setSerialNumber("");
        setBrand("");
        setModel("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);



    const onNewTaskClick = () => {
        navigate(Routes.auxiliaryEquipments.create);
    };



    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.AuxiliaryEquipments.Title" />
                        </span>

                        <Row>
                            <Col
                                xl={2}
                                lg={3}
                                md={3}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", marginBottom: "1em" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.AuxiliaryEquipments.Button.CreateAuxiliaryEquipment" })}
                                    onClick={onNewTaskClick}
                                    disabled={false} />
                            </Col>
                        </Row>
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={erpId}
                                label={intl.formatMessage({ id: "Screens.AuxiliaryEquipments.SearchField1" })}
                                readOnly={false}
                                onChange={(t: string) => setErpId(t)}
                            />
                            <InputComponent
                                xl={4}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={description}
                                label={intl.formatMessage({ id: "Screens.AuxiliaryEquipments.SearchField2" })}
                                readOnly={false}
                                onChange={(t: string) => setDescription(t)}
                            />
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={serialNumber}
                                label={intl.formatMessage({ id: "Screens.AuxiliaryEquipments.SearchField3" })}
                                readOnly={false}
                                onChange={(t: string) => setSerialNumber(t)}
                            />
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={brand}
                                label={intl.formatMessage({ id: "Screens.AuxiliaryEquipments.SearchField4" })}
                                readOnly={false}
                                onChange={(t: string) => setBrand(t)}
                            />
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={model}
                                label={intl.formatMessage({ id: "Screens.AuxiliaryEquipments.SearchField5" })}
                                readOnly={false}
                                onChange={(t: string) => setModel(t)}
                            />
                        </Row>

                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                    style={{ minWidth: "100px" }}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                    style={{ minWidth: "113px" }}
                                />
                            </Col>
                        </Row>

                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
